/**
 * Centralized Filter Definitions
 *
 * Single source of truth for filter options and filtering logic.
 * All UI components and filtering must import from this file.
 */

import { Gym, FilterState } from '../types';

// ============================================
// CANONICAL FILTER OPTIONS
// ============================================

export const STYLE_OPTIONS = [
    "Aikido",
    "Boxing",
    "BJJ",
    "Capoeira",
    "Hapkido",
    "Judo",
    "Karate",
    "Kendo",
    "Kickboxing",
    "Krav Maga",
    "Kung Fu",
    "MMA",
    "Muay Thai",
    "Taekwondo",
    "Wrestling"
] as const;

export const AGE_OPTIONS = [
    "Children (5-12)",
    "Teens (13-17)",
    "Adults (18+)"
] as const;

export const EXPERIENCE_OPTIONS = [
    "Beginner",
    "Intermediate",
    "Advanced"
] as const;

// Type definitions from canonical options
export type StyleOption = typeof STYLE_OPTIONS[number];
export type AgeOption = typeof AGE_OPTIONS[number];
export type ExperienceOption = typeof EXPERIENCE_OPTIONS[number];



// ============================================
// GYM VIEW MODEL (Gym + Enrichment Status)
// ============================================

export type EnrichmentStatus = 'ready' | 'missing' | 'pending' | 'error';

export interface GymTags {
    styles: string[];
    ageGroups: string[];
    experienceLevels: string[];
}

export interface GymViewModel {
    gym: Gym;
    tags: GymTags;
}

// ============================================
// SELECTED FILTERS (for filtering function)
// ============================================

export interface SelectedFilters {
    styles: string[];
    ageGroups: string[];
    experienceLevels: string[];
    gymName: string;
}

// ============================================
// NORMALIZATION HELPERS
// ============================================

/**
 * Normalize a filter value for comparison (trim, lowercase)
 */
const normalizeFilter = (value: string): string => {
    return value.trim().toLowerCase();
};

/**
 * Check if a gym's tags contain at least one of the selected filter values.
 * `exact` uses case-insensitive equality; otherwise case-insensitive substring
 * matching. Styles pass exact=true because STYLE_OPTIONS has an intra-set
 * substring collision ("Boxing" ⊂ "Kickboxing") that substring matching would
 * cross-match — a Kickboxing-only gym wrongly matching a "Boxing" filter, and
 * vice versa. Age/experience keep substring matching: they have no such
 * collision, and substring preserves partial/legacy tags and non-canonical
 * URL params (e.g. ?age=Adults → "Adults (18+)").
 */
const matchesAny = (gymTags: string[], selectedFilters: string[], exact = false): boolean => {
    if (selectedFilters.length === 0) return true;

    const normalizedGymTags = gymTags.map(normalizeFilter);
    const normalizedFilters = selectedFilters.map(normalizeFilter);

    return normalizedFilters.some(filter =>
        normalizedGymTags.some(tag =>
            exact ? tag === filter : (tag.includes(filter) || filter.includes(tag))
        )
    );
};

// ============================================
// MAIN FILTERING FUNCTION
// ============================================

/**
 * Apply filters to a list of GymViewModels.
 *
 * TAG-BASED EXCLUSION:
 * - A tag filter (styles / ageGroups / experienceLevels) excludes a gym only when
 *   that gym has NO tags for that dimension, or its tags don't match the selection.
 * - When no filter is selected for a dimension, that dimension excludes nothing.
 * - The gym-name filter matches case-insensitively on the gym name.
 *
 * @param gyms - Array of GymViewModels (gym + tags)
 * @param filters - Selected filter values
 * @returns Filtered array of GymViewModels
 */
export function applyFilters(
    gyms: GymViewModel[],
    filters: SelectedFilters
): GymViewModel[] {
    const hasStyleFilter = filters.styles.length > 0;
    const hasAgeFilter = filters.ageGroups.length > 0;
    const hasExperienceFilter = filters.experienceLevels.length > 0;
    const hasGymNameFilter = !!filters.gymName;

    return gyms.filter(vm => {
        // Gym name filter (always applies, uses gym name directly)
        if (hasGymNameFilter) {
            const nameMatch = vm.gym.name.toLowerCase().includes(filters.gymName.toLowerCase());
            if (!nameMatch) return false;
        }

        // For tag-based filters, check if gym has any tags to filter on
        // (either from enrichment or from fallback gym data)
        const hasTags = (arr: string[] | undefined) => arr && arr.length > 0;

        // Style filter: If filter is set, gym must have styles AND match
        if (hasStyleFilter) {
            if (!hasTags(vm.tags.styles)) return false;
            if (!matchesAny(vm.tags.styles, filters.styles, true)) return false;
        }

        // Age filter: If filter is set, gym must have ageGroups AND match
        if (hasAgeFilter) {
            if (!hasTags(vm.tags.ageGroups)) return false;
            if (!matchesAny(vm.tags.ageGroups, filters.ageGroups)) return false;
        }

        // Experience filter: multi-select — gym must match at least one selected level
        if (hasExperienceFilter) {
            if (!hasTags(vm.tags.experienceLevels)) return false;
            if (!matchesAny(vm.tags.experienceLevels, filters.experienceLevels)) return false;
        }

        return true;
    });
}

// ============================================
// HELPER: Convert FilterState to SelectedFilters
// ============================================

export function toSelectedFilters(filterState: FilterState): SelectedFilters {
    return {
        styles: filterState.styles || [],
        ageGroups: filterState.ageGroups || [],
        experienceLevels: filterState.experienceLevels || [],
        gymName: filterState.gymName || ''
    };
}
